import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import UrgencyTicker from '@/components/UrgencyTicker';
import {
  Menu, X, LogOut, User, Settings, Workflow, Briefcase,
  TrendingUp, GraduationCap, FileText, Rocket, DollarSign, Shield,
  ChevronDown, Award, Trophy, Globe2, Layers, BookOpen,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* =====================================================================
   SKI AXIS — Global Header (cinematic dark/gold, synced with landing)
   Inspiration: capitalwealthsummit.com — pill nav, gold CTA
===================================================================== */

const HEADER_STYLES = `
.skia-header *{box-sizing:border-box;}
.skia-header{
  --ink:#FAF7F0;--ink2:#F4EFE6;--ink3:#FFFFFF;
  --gold:#C8A84B;--gold2:#B5944A;
  --cream:#0D1B2A;--mist:#3A4A5C;--stone:#7A8190;
  position:sticky;top:0;z-index:200;background:rgba(250,247,240,.92);
  backdrop-filter:blur(24px);-webkit-backdrop-filter:blur(24px);
  border-bottom:1px solid rgba(200,168,75,.12);
  font-family:'DM Sans',-apple-system,BlinkMacSystemFont,sans-serif;
}
.skia-header .h-inner{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;height:74px;padding:0 32px;}
@media (max-width:768px){.skia-header .h-inner{padding:0 20px;height:64px;}}
.skia-header .h-brand{display:flex;flex-direction:column;cursor:pointer;line-height:1;}
.skia-header .h-brand-row{display:flex;align-items:baseline;gap:8px;}
.skia-header .h-logo-mark{font-family:'Cormorant Display',Georgia,serif;font-size:24px;font-weight:700;letter-spacing:.06em;color:var(--gold);}
.skia-header .h-logo-mark em{font-style:italic;font-weight:300;color:var(--cream);margin-left:2px;}
.skia-header .h-logo-sub{font-size:8px;font-weight:600;letter-spacing:.28em;text-transform:uppercase;color:var(--stone);margin-top:5px;}
.skia-header .h-pill{display:flex;align-items:center;gap:0;background:var(--ink3);border:1px solid rgba(13,27,42,.07);border-radius:50px;padding:0 4px;height:42px;}
.skia-header .h-pill button{font-family:inherit;font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--stone);padding:0 16px;height:34px;display:flex;align-items:center;gap:6px;border-radius:50px;transition:all .2s;white-space:nowrap;cursor:pointer;background:transparent;border:none;}
.skia-header .h-pill button:hover,.skia-header .h-pill button.active{color:var(--cream);background:rgba(13,27,42,.05);}
.skia-header .h-pill .h-sep{width:1px;height:14px;background:rgba(13,27,42,.07);flex-shrink:0;}
.skia-header .h-cta{background:var(--gold);color:var(--ink);font-family:inherit;font-size:10px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;padding:0 22px;height:36px;border-radius:50px;border:none;cursor:pointer;transition:all .2s;display:inline-flex;align-items:center;gap:6px;}
.skia-header .h-cta:hover{background:var(--gold2);transform:scale(1.02);box-shadow:0 4px 20px rgba(200,168,75,.3);}
.skia-header .h-signin{font-family:inherit;font-size:11px;font-weight:600;letter-spacing:.14em;text-transform:uppercase;color:var(--stone);padding:0 16px;cursor:pointer;background:transparent;border:none;}
.skia-header .h-signin:hover{color:var(--cream);}
.skia-header .h-userbtn{font-family:inherit;background:var(--ink3);border:1px solid rgba(200,168,75,.15);color:var(--cream);font-size:11px;font-weight:600;letter-spacing:.06em;padding:0 14px;height:36px;border-radius:50px;cursor:pointer;display:inline-flex;align-items:center;gap:8px;transition:all .2s;}
.skia-header .h-userbtn:hover{border-color:rgba(200,168,75,.4);}
.skia-header .h-userbtn-dot{width:8px;height:8px;border-radius:50%;background:var(--gold);}
.skia-header .h-mobile-btn{display:none;background:transparent;border:1px solid rgba(13,27,42,.1);color:var(--cream);width:42px;height:42px;border-radius:50%;align-items:center;justify-content:center;cursor:pointer;}
@media (max-width:1024px){.skia-header .h-pill,.skia-header .h-signin,.skia-header .h-cta{display:none;}.skia-header .h-mobile-btn{display:inline-flex;}}
.skia-header .h-mobile-panel{display:none;background:var(--ink2);border-top:1px solid rgba(200,168,75,.12);padding:24px;flex-direction:column;gap:8px;}
.skia-header .h-mobile-panel.open{display:flex;}
.skia-header .h-mobile-link{font-family:inherit;font-size:13px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--cream);background:transparent;border:none;text-align:left;padding:14px 16px;border-radius:8px;cursor:pointer;display:flex;align-items:center;gap:12px;}
.skia-header .h-mobile-link:hover,.skia-header .h-mobile-link.active{background:rgba(200,168,75,.08);color:var(--gold);}
.skia-header .h-mobile-cta{background:var(--gold);color:var(--ink);font-family:inherit;font-size:11px;font-weight:800;letter-spacing:.18em;text-transform:uppercase;padding:14px 22px;border-radius:50px;border:none;cursor:pointer;margin-top:12px;}

/* PROGRAMMES dropdown trigger inside the pill */
.skia-header .h-prog-trigger{font-family:inherit;font-size:11px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:var(--stone);padding:0 14px;height:34px;display:flex;align-items:center;gap:6px;border-radius:50px;background:transparent;border:none;cursor:pointer;transition:all .2s;white-space:nowrap;}
.skia-header .h-prog-trigger:hover,.skia-header .h-prog-trigger.active{color:var(--cream);background:rgba(13,27,42,.05);}
.skia-header .h-prog-trigger svg{transition:transform .25s;}
.skia-header .h-prog-trigger[data-state="open"] svg{transform:rotate(180deg);color:var(--gold);}

/* PROGRAMMES dropdown menu (cream/navy/gold editorial) */
.skia-prog-menu{background:#FAF7F0 !important;border:1px solid rgba(200,168,75,.25) !important;color:#0D1B2A !important;padding:10px !important;min-width:340px !important;box-shadow:0 24px 48px rgba(13,27,42,.14) !important;}
.skia-prog-menu .skia-prog-eb{font-size:9.5px;font-weight:800;letter-spacing:.28em;color:#C8A84B;text-transform:uppercase;padding:8px 12px 4px;display:block;}
.skia-prog-menu .skia-prog-item{display:flex;align-items:flex-start;gap:12px;padding:11px 12px;border-radius:6px;cursor:pointer;transition:all .15s;color:#0D1B2A;}
.skia-prog-menu .skia-prog-item:hover{background:rgba(200,168,75,.08);}
.skia-prog-menu .skia-prog-icn{width:32px;height:32px;border-radius:6px;background:rgba(200,168,75,.1);color:#C8A84B;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.skia-prog-menu .skia-prog-text{display:flex;flex-direction:column;gap:2px;line-height:1.3;}
.skia-prog-menu .skia-prog-title{font-family:'DM Sans',sans-serif;font-size:13.5px;font-weight:700;color:#0D1B2A;letter-spacing:0;text-transform:none;}
.skia-prog-menu .skia-prog-title em{font-family:'Cormorant Display',serif;font-style:italic;font-weight:600;color:#C8A84B;letter-spacing:0;}
.skia-prog-menu .skia-prog-sub{font-family:'Cormorant Display',serif;font-style:italic;font-size:12px;color:#3D4A5C;line-height:1.4;}
.skia-prog-menu .skia-prog-sep{height:1px;background:rgba(200,168,75,.18);margin:6px 8px;}

/* MOBILE programmes accordion */
.skia-header .h-mobile-prog{display:flex;flex-direction:column;}
.skia-header .h-mobile-prog-head{display:flex;align-items:center;justify-content:space-between;font-family:inherit;font-size:13px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--cream);background:transparent;border:none;text-align:left;padding:14px 16px;border-radius:8px;cursor:pointer;}
.skia-header .h-mobile-prog-head svg{transition:transform .25s;}
.skia-header .h-mobile-prog-head[aria-expanded="true"] svg{transform:rotate(180deg);color:var(--gold);}
.skia-header .h-mobile-prog-list{display:none;flex-direction:column;gap:2px;padding-left:14px;border-left:2px solid rgba(200,168,75,.2);margin-left:16px;}
.skia-header .h-mobile-prog-list.open{display:flex;}
.skia-header .h-mobile-prog-list button{font-family:inherit;font-size:12.5px;font-weight:600;letter-spacing:.04em;text-transform:none;color:var(--cream);background:transparent;border:none;text-align:left;padding:11px 12px;border-radius:6px;cursor:pointer;}
.skia-header .h-mobile-prog-list button:hover{background:rgba(200,168,75,.08);color:var(--gold);}
.skia-header .h-mobile-prog-list button em{font-family:'Cormorant Display',serif;font-style:italic;color:var(--gold);}
`;

export const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProgOpen, setMobileProgOpen] = useState(false);

  // PROGRAMMES dropdown — six routes (Cohort 1 + The Axis Index + The Axis Circuit + Masterclass + Chapters + Accelerator)
  const programmes = [
    { path: '/apply',        icon: <Rocket className="h-4 w-4" />,       title: 'Cohort 1',                  sub: 'July 2026 · 20 chosen founders' },
    { path: '/axis-index',   icon: <Award className="h-4 w-4" />,        titleEm: 'The Axis Index™',         sub: 'Governance maturity framework' },
    { path: '/axis-circuit', icon: <Trophy className="h-4 w-4" />,       titleEm: 'The Axis Circuit™',       sub: 'Global tournament · Q4 2027 finale' },
    { path: '/masterclass',  icon: <GraduationCap className="h-4 w-4"/>, title: 'Masterclass',               sub: 'Live + recorded faculty sessions' },
    { path: '/chapters',     icon: <Globe2 className="h-4 w-4" />,       title: 'Chapter Leads',             sub: '11 cities · now appointing' },
    { path: '/accelerator',  icon: <Layers className="h-4 w-4" />,       title: 'Accelerator Overview',      sub: 'How the 12-week programme runs' },
    { path: '/programmes',   icon: <BookOpen className="h-4 w-4" />,     title: 'View all · 30-second self-quiz', sub: 'Find the right programme for you', divider: true },
  ];

  // Top-level pill items (kept lean — discovery via PROGRAMMES dropdown)
  const baseNav = [
    { path: '/webinars', label: 'Webinars' },
    { path: '/events/invest-uae-forum-2026', label: 'UAE Forum' },
    { path: '/mentors', label: 'Mentors' },
    { path: '/founders', label: 'Founders' },
    { path: '/jobs', label: 'Jobs' },
    { path: '/blog', label: 'Blog' },
    { path: '/community', label: 'Community' },
    { path: '/pricing', label: 'Pricing' },
  ];
  const navItems = user?.role === 'admin'
    ? [...baseNav, { path: '/admin', label: 'Admin' }]
    : baseNav;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const isProgrammesActive = programmes.some((p) => isActive(p.path));

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: HEADER_STYLES }} />
      <UrgencyTicker />
      <header className="skia-header">
        <div className="h-inner">
          <div className="h-brand" onClick={() => navigate('/')} data-testid="logo-btn">
            <div className="h-brand-row">
              <span className="h-logo-mark">SKI<em>AXIS</em></span>
            </div>
            <span className="h-logo-sub">Governance · Innovation · AI</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <nav className="h-pill" aria-label="Primary">
              {/* PROGRAMMES dropdown — leads the pill */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={`h-prog-trigger ${isProgrammesActive ? 'active' : ''}`}
                    data-testid="nav-programmes"
                  >
                    Programmes <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" sideOffset={10} className="skia-prog-menu">
                  <span className="skia-prog-eb">Programmes · Six paths in</span>
                  {programmes.map((p, i) => (
                    <React.Fragment key={p.path}>
                      {(i === 3 || p.divider) && <div className="skia-prog-sep" />}
                      <div
                        className="skia-prog-item"
                        onClick={() => navigate(p.path)}
                        data-testid={`nav-prog-${p.path.replace(/[/]/g, '-').replace(/^-/, '')}`}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter') navigate(p.path); }}
                      >
                        <div className="skia-prog-icn">{p.icon}</div>
                        <div className="skia-prog-text">
                          <div className="skia-prog-title">
                            {p.titleEm ? <em>{p.titleEm}</em> : p.title}
                          </div>
                          <div className="skia-prog-sub">{p.sub}</div>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {navItems.map((item) => (
                <React.Fragment key={item.path}>
                  <span className="h-sep" />
                  <button
                    className={isActive(item.path) ? 'active' : ''}
                    onClick={() => navigate(item.path)}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.label}
                  </button>
                </React.Fragment>
              ))}
            </nav>

            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-userbtn" data-testid="user-menu">
                    <span className="h-userbtn-dot" />
                    <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {user?.full_name || user?.email}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-56 bg-[#0D0D12] border-[rgba(200,168,75,0.15)] text-[#F0E8D4]"
                >
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span className="text-[#F0E8D4]">{user?.full_name}</span>
                      <span className="text-xs font-normal text-[#B8B0A4]">{user?.email}</span>
                      <span className="text-xs font-medium mt-1 text-[#C8A84B]">
                        {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                        {!user?.is_approved && <span className="text-yellow-500 ml-1">(Pending)</span>}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-[rgba(200,168,75,0.15)]" />
                  {user?.role === 'founder' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/apply')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <FileText className="h-4 w-4 mr-2" /> Apply to Program
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/company-jobs')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <Briefcase className="h-4 w-4 mr-2" /> My Job Postings
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'admin' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <Settings className="h-4 w-4 mr-2" /> Admin Panel
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin/events')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <Briefcase className="h-4 w-4 mr-2" /> Event Registrations
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/workflows')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <Workflow className="h-4 w-4 mr-2" /> AI Workflows
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/financials')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <DollarSign className="h-4 w-4 mr-2" /> Financials
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'investor' && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/investor')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <TrendingUp className="h-4 w-4 mr-2" /> Investor Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/investor-pipeline')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                        <Briefcase className="h-4 w-4 mr-2" /> Deal Pipeline
                      </DropdownMenuItem>
                    </>
                  )}
                  {user?.role === 'mentor' && (
                    <DropdownMenuItem onClick={() => navigate('/mentor-registration')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                      <GraduationCap className="h-4 w-4 mr-2" /> Masterclass Registration
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/governance')} className="focus:bg-[rgba(200,168,75,0.08)] focus:text-[#C8A84B]">
                    <Shield className="h-4 w-4 mr-2" /> Dashboard
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-[rgba(200,168,75,0.15)]" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:bg-red-500/10 focus:text-red-300">
                    <LogOut className="h-4 w-4 mr-2" /> Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <button className="h-signin" onClick={() => navigate('/auth')} data-testid="login-btn">
                  Sign In
                </button>
                <button className="h-cta" onClick={() => navigate('/auth?mode=signup')} data-testid="signup-btn">
                  <Rocket className="h-3 w-3" /> Get Started
                </button>
              </>
            )}

            <button
              className="h-mobile-btn"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-btn"
              aria-label="Menu"
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        <div className={`h-mobile-panel ${mobileOpen ? 'open' : ''}`}>
          {isAuthenticated && (
            <div style={{
              padding: '14px 16px',
              borderRadius: 8,
              background: 'rgba(200,168,75,0.06)',
              border: '1px solid rgba(200,168,75,0.15)',
              marginBottom: 8
            }}>
              <div style={{ color: '#F0E8D4', fontSize: 14, fontWeight: 600 }}>{user?.full_name}</div>
              <div style={{ color: '#C8A84B', fontSize: 11, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', marginTop: 2 }}>
                {user?.role}
              </div>
            </div>
          )}
          {navItems.length > 0 && (
            <div className="h-mobile-prog">
              <button
                className="h-mobile-prog-head"
                aria-expanded={mobileProgOpen}
                onClick={() => setMobileProgOpen((v) => !v)}
                data-testid="mobile-nav-programmes"
              >
                <span>Programmes</span>
                <ChevronDown className="h-4 w-4" />
              </button>
              <div className={`h-mobile-prog-list ${mobileProgOpen ? 'open' : ''}`}>
                {programmes.map((p) => (
                  <button
                    key={p.path}
                    onClick={() => { navigate(p.path); setMobileOpen(false); setMobileProgOpen(false); }}
                    data-testid={`mobile-nav-prog-${p.path.replace(/[/]/g, '-').replace(/^-/, '')}`}
                  >
                    {p.titleEm ? <em>{p.titleEm}</em> : p.title}
                  </button>
                ))}
              </div>
            </div>
          )}
          {navItems.map((item) => (
            <button
              key={item.path}
              className={`h-mobile-link ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => { navigate(item.path); setMobileOpen(false); }}
              data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {item.label}
            </button>
          ))}
          {isAuthenticated ? (
            <button
              className="h-mobile-link"
              style={{ color: '#ef4444' }}
              onClick={() => { handleLogout(); setMobileOpen(false); }}
            >
              <LogOut className="h-4 w-4" /> Sign Out
            </button>
          ) : (
            <>
              <button className="h-mobile-link" onClick={() => { navigate('/auth'); setMobileOpen(false); }}>
                <User className="h-4 w-4" /> Sign In
              </button>
              <button className="h-mobile-cta" onClick={() => { navigate('/auth?mode=signup'); setMobileOpen(false); }}>
                Get Started
              </button>
            </>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
